import jsonpatch from 'fast-json-patch';
import type { Operation } from 'fast-json-patch';

export type JsonPatchSuccess<T> = {
  ok: true;
  value: T;
  operationCount?: number;
};

export type JsonPatchFailure = {
  ok: false;
  message: string;
  operationIndex?: number;
  // Human-first fields, present when the failure came from the patch library.
  headline?: string;
  hint?: string;
  technical?: string;
};

export type JsonPatchResult<T> = JsonPatchSuccess<T> | JsonPatchFailure;

export type VisualDiffItem = {
  op: string;
  path: string;
  from?: string;
  before?: unknown;
  after?: unknown;
};

type LibraryPatchError = {
  name?: string;
  index?: number;
  operation?: Operation;
  tree?: unknown;
  message?: string;
};

const OPERATION_ERROR_REASONS: Record<string, string> = {
  OPERATION_PATH_UNRESOLVABLE: "that path doesn't exist in the source JSON",
  OPERATION_FROM_UNRESOLVABLE: "the 'from' path doesn't exist in the source JSON",
  OPERATION_OP_REQUIRED: 'the operation is missing its "op"',
  OPERATION_OP_INVALID: 'the "op" is not a valid JSON Patch operation',
  OPERATION_PATH_REQUIRED: 'the operation is missing its "path"',
  OPERATION_PATH_INVALID: 'the "path" is not a valid JSON pointer',
  OPERATION_VALUE_REQUIRED: 'the operation needs a "value"',
  OPERATION_VALUE_CANNOT_CONTAIN_UNDEFINED: 'the "value" cannot be undefined',
  OPERATION_FROM_REQUIRED: 'the operation needs a "from" path',
  OPERATION_NOT_AN_INDEX: 'the path needs a valid array index',
  TEST_OPERATION_FAILED: 'the test failed - the source value does not match',
};

const levenshtein = (a: string, b: string): number => {
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  const curr = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }

  return prev[b.length];
};

const similarity = (a: string, b: string): number => {
  if (!a.length && !b.length) return 1;
  return 1 - levenshtein(a, b) / Math.max(a.length, b.length);
};

const listPointerPaths = (tree: unknown): string[] => {
  const paths: string[] = [];

  const walk = (node: unknown, base: string) => {
    if (node === null || typeof node !== 'object') return;
    for (const key of Object.keys(node as Record<string, unknown>)) {
      const pointer = `${base}/${key.replace(/~/g, '~0').replace(/\//g, '~1')}`;
      paths.push(pointer);
      walk((node as Record<string, unknown>)[key], pointer);
    }
  };

  walk(tree, '');
  return paths;
};

// Suggests the closest existing path when the patch points somewhere that
// does not resolve - catches simple typos like /preferences/themee.
const suggestPath = (path: string, tree: unknown): string | undefined => {
  let best: string | undefined;
  let bestScore = 0;

  for (const candidate of listPointerPaths(tree)) {
    const score = similarity(path, candidate);
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best && bestScore >= 0.6 ? best : undefined;
};

const humanizeLibraryError = (error: LibraryPatchError): Pick<JsonPatchFailure, 'headline' | 'hint' | 'technical'> => {
  const operation = error.operation;
  const index = typeof error.index === 'number' ? error.index : undefined;
  const opLabel = operation ? `${operation.op ?? '?'} ${operation.path ?? ''}`.trim() : '';
  const reason = (error.name && OPERATION_ERROR_REASONS[error.name]) || error.message || 'the patch could not be applied';

  const headline = index !== undefined
    ? `Operation ${index + 1}${opLabel ? ` (${opLabel})` : ''}: ${reason}`
    : `Patch failed: ${reason}`;

  let hint: string | undefined;
  if (
    operation?.path &&
    error.tree !== undefined &&
    (error.name === 'OPERATION_PATH_UNRESOLVABLE' || error.name === 'OPERATION_FROM_UNRESOLVABLE')
  ) {
    const suggestion = suggestPath(operation.path, error.tree);
    if (suggestion) hint = `Did you mean "${suggestion}"?`;
  }

  let technical = JSON.stringify(
    { name: error.name, index, operation, tree: error.tree },
    null,
    2,
  );
  if (technical.length > 4000) technical = `${technical.slice(0, 4000)}\n... (truncated)`;

  return { headline, hint, technical };
};

const formatError = (message: string, error: unknown): JsonPatchFailure => {
  const patchError = error as LibraryPatchError;

  const failure: JsonPatchFailure = {
    ok: false,
    message: patchError.message ? `${message}: ${patchError.message}` : message,
    operationIndex: typeof patchError.index === 'number' ? patchError.index : undefined,
  };

  // Only library errors carry index + operation; JSON.parse failures do not,
  // so parse errors keep the plain message treatment.
  if (patchError && typeof patchError.index === 'number' && patchError.operation) {
    Object.assign(failure, humanizeLibraryError(patchError));
  }

  return failure;
};

const parseJson = <T = unknown>(value: string, label: string): JsonPatchResult<T> => {
  try {
    return { ok: true, value: JSON.parse(value) as T };
  } catch (error) {
    return formatError(`${label} is not valid JSON`, error);
  }
};

const parsePatch = (patchJson: string): JsonPatchResult<Operation[]> => {
  const parsed = parseJson<unknown>(patchJson, 'Patch');

  if (!parsed.ok) {
    return parsed as JsonPatchFailure;
  }

  if (!Array.isArray(parsed.value)) {
    return { ok: false, message: 'Patch must be a JSON array of RFC 6902 operations.' };
  }

  return { ok: true, value: parsed.value as Operation[], operationCount: parsed.value.length };
};

const stringifyJson = (value: unknown): string => JSON.stringify(value, null, 2);

// Deep equality for JSON values - replaces fast-json-patch's private
// _areEquals so a library upgrade can't silently break verification.
// JSON semantics: objects compare as unordered key sets, arrays are ordered,
// primitives compare with === (so -0 equals 0, and '1' does not equal 1).
export const jsonDeepEquals = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;

  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }

  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => jsonDeepEquals(item, b[index]))
    );
  }

  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);

  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(bRecord, key) &&
      jsonDeepEquals(aRecord[key], bRecord[key]),
  );
};

// Explains WHERE and HOW an applied result diverges from the expected target,
// using fast-json-patch.compare which yields the operations that would turn
// the applied result into the target document.
const describeFirstMismatch = (resultValue: unknown, targetValue: unknown): string => {
  const prefix = 'Verification failed - applied result does not match target JSON.';

  let diffs;
  try {
    diffs = jsonpatch.compare(resultValue as object, targetValue as object);
  } catch {
    return `${prefix}`;
  }

  const first = diffs[0];
  if (!first) return prefix;

  const path = first.path ? `"${first.path}"` : 'the document root';
  const readActual = (): unknown => {
    try {
      return jsonpatch.getValueByPointer(resultValue, first.path);
    } catch {
      return undefined;
    }
  };

  switch (first.op) {
    case 'add':
      return `${prefix} ${path} is missing from the applied result - Target expects ${JSON.stringify(first.value)}.`;
    case 'remove': {
      const extra = readActual();
      return `${prefix} ${path} exists in the applied result but not in Target${extra === undefined ? '' : ` (${JSON.stringify(extra)} should be removed)`}.`;
    }
    case 'replace':
      return `${prefix} ${path} differs - Target expects ${JSON.stringify(first.value)}, but the patch produces ${JSON.stringify(readActual())}.`;
    default:
      return `${prefix} ${path} differs from Target (${first.op} operation would be required to reconcile them).`;
  }
};

export function generateJsonPatch(sourceJson: string, targetJson: string): JsonPatchResult<string> {
  const source = parseJson<object | unknown[]>(sourceJson, 'Source JSON');
  if (!source.ok) return source as JsonPatchFailure;

  const target = parseJson<object | unknown[]>(targetJson, 'Target JSON');
  if (!target.ok) return target as JsonPatchFailure;

  try {
    const patch = jsonpatch.compare(source.value, target.value);
    return {
      ok: true,
      value: stringifyJson(patch),
      operationCount: patch.length,
    };
  } catch (error) {
    return formatError('Unable to generate JSON Patch', error);
  }
}

export function applyPatchToJson(sourceJson: string, patchJson: string): JsonPatchResult<string> {
  const source = parseJson<unknown>(sourceJson, 'Source JSON');
  if (!source.ok) return source as JsonPatchFailure;

  const patch = parsePatch(patchJson);
  if (!patch.ok) return patch as JsonPatchFailure;

  try {
    const result = jsonpatch.applyPatch(source.value, patch.value, true, false, true);

    return {
      ok: true,
      value: stringifyJson(result.newDocument),
      operationCount: patch.value.length,
    };
  } catch (error) {
    return formatError('Patch operation failed', error);
  }
}

export function verifyPatch(sourceJson: string, targetJson: string, patchJson: string): JsonPatchResult<string> {
  const applied = applyPatchToJson(sourceJson, patchJson);
  if (!applied.ok) return applied;

  const target = parseJson<unknown>(targetJson, 'Target JSON');
  if (!target.ok) return target as JsonPatchFailure;

  const result = parseJson<unknown>(applied.value, 'Applied result');
  if (!result.ok) return result as JsonPatchFailure;

  if (!jsonDeepEquals(result.value, target.value)) {
    return {
      ok: false,
      message: describeFirstMismatch(result.value, target.value),
    };
  }

  return {
    ok: true,
    value: applied.value,
    operationCount: applied.operationCount,
  };
}

export function getVisualDiff(patchJson: string, sourceJson?: string): JsonPatchResult<VisualDiffItem[]> {
  const patch = parsePatch(patchJson);
  if (!patch.ok) return patch;

  const source = sourceJson?.trim() ? parseJson<unknown>(sourceJson, 'Source JSON') : null;
  const sourceValue = source && source.ok ? source.value : undefined;

  const items = patch.value.map((operation) => {
    const item: VisualDiffItem = {
      op: operation.op,
      path: operation.path,
    };

    if ('from' in operation) {
      item.from = operation.from;
    }

    if ('value' in operation) {
      item.after = operation.value;
    }

    if (sourceValue !== undefined && operation.path) {
      try {
        item.before = jsonpatch.getValueByPointer(sourceValue, operation.path);
      } catch {
        // Some paths intentionally do not exist before add operations.
      }
    }

    return item;
  });

  return { ok: true, value: items, operationCount: items.length };
}
